import React, { useEffect, useRef, useState, useCallback } from "react";

function TileVisualizer() {
  const canvasRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const [tileImage, setTileImage] = useState(null);
  const [wallImage, setWallImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [wallImageObj, setWallImageObj] = useState(null);
  const [tileImageObj, setTileImageObj] = useState(null);

  useEffect(() => {
    // Initialize HTML5 Canvas
    const canvasElement = canvasRef.current;
    const ctx = canvasElement.getContext('2d');
    setCanvas(ctx);

    // Set canvas size
    canvasElement.width = 800;
    canvasElement.height = 600;

    // Set background
    ctx.fillStyle = '#f5f5f5';
    ctx.fillRect(0, 0, canvasElement.width, canvasElement.height);
  }, []);

  // Function to draw canvas with proper async handling
  const drawCanvas = useCallback(() => {
    if (!canvas) return;

    const canvasElement = canvasRef.current;
    canvas.clearRect(0, 0, canvasElement.width, canvasElement.height);

    // Draw background
    canvas.fillStyle = '#f5f5f5';
    canvas.fillRect(0, 0, canvasElement.width, canvasElement.height);

    // Draw wall image if exists
    if (wallImageObj) {
      canvas.drawImage(wallImageObj, 0, 0, canvasElement.width, canvasElement.height);
    }

    // Draw tiles on top
    tiles.forEach(tile => {
      if (tile.imageObj) {
        canvas.globalAlpha = tile.opacity || 0.8;
        canvas.drawImage(tile.imageObj, tile.x, tile.y, tile.width, tile.height);
        canvas.globalAlpha = 1;
      }
    });
  }, [canvas, wallImageObj, tiles]);

  // Upload wall photo
  const handleWallUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    const reader = new FileReader();
    reader.onload = function (event) {
      const img = new Image();
      img.onload = () => {
        setWallImageObj(img);
        setWallImage(event.target.result);
        setIsLoading(false);
      };
      img.onerror = () => {
        console.error("Wall image load failed");
        setIsLoading(false);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Upload tile photo
  const handleTileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    const reader = new FileReader();
    reader.onload = function (event) {
      const img = new Image();
      img.onload = () => {
        setTileImageObj(img);
        setTileImage(event.target.result);
        setIsLoading(false);
      };
      img.onerror = () => {
        console.error("Tile image load failed");
        setIsLoading(false);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Apply single tile
  const addSingleTile = () => {
    if (!tileImageObj || !canvas) {
      alert("Pehle tile image upload kariye!");
      return;
    }

    const newTile = {
      id: Date.now(),
      imageObj: tileImageObj,
      x: Math.random() * 600 + 100, // Random position
      y: Math.random() * 400 + 100,
      width: 100,
      height: 100,
      opacity: 0.9
    };
    setTiles(prev => [...prev, newTile]);
  };

  // Apply pattern fill
  const applyPatternFill = () => {
    if (!tileImageObj || !canvas) {
      alert("Pehle tile image upload kariye!");
      return;
    }

    setIsLoading(true);
    const canvasElement = canvasRef.current;
    const tileWidth = 80;
    const tileHeight = 80;
    const newTiles = [];

    for (let x = 0; x < canvasElement.width; x += tileWidth) {
      for (let y = 0; y < canvasElement.height; y += tileHeight) {
        newTiles.push({
          id: `pattern_${x}_${y}`,
          imageObj: tileImageObj,
          x: x,
          y: y,
          width: tileWidth,
          height: tileHeight,
          opacity: 0.8
        });
      }
    }

    setTiles(newTiles);
    setIsLoading(false);
  };

  // Create grid pattern
  const createGridPattern = () => {
    if (!tileImageObj || !canvas) {
      alert("Pehle tile image upload kariye!");
      return;
    }

    setIsLoading(true);
    const canvasElement = canvasRef.current;
    const cols = 8;
    const rows = 6;
    const tileWidth = canvasElement.width / cols;
    const tileHeight = canvasElement.height / rows;
    const newTiles = [];

    for (let col = 0; col < cols; col++) {
      for (let row = 0; row < rows; row++) {
        newTiles.push({
          id: `grid_${col}_${row}`,
          imageObj: tileImageObj,
          x: col * tileWidth,
          y: row * tileHeight,
          width: tileWidth,
          height: tileHeight,
          opacity: 0.85
        });
      }
    }

    setTiles(newTiles);
    setIsLoading(false);
  };

  // Clear all tiles
  const clearTiles = () => {
    setTiles([]);
  };

  // Draw everything when dependencies change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white rounded-lg shadow-lg">
      <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
        🏠 Tile Wall Visualizer
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Upload Section */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-3">📷 Upload Images</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wall Image (Background)
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleWallUpload}
                className="w-full p-2 border border-gray-300 rounded"
              />
              {wallImage && (
                <div className="mt-2 text-green-600 text-sm">✅ Wall image loaded</div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tile Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleTileUpload}
                className="w-full p-2 border border-gray-300 rounded"
              />
              {tileImage && (
                <div className="mt-2 text-green-600 text-sm">✅ Tile image loaded</div>
              )}
            </div>
          </div>
        </div>

        {/* Controls Section */}
        <div className="bg-green-50 p-4 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-3">🎨 Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={addSingleTile}
              disabled={!tileImageObj || isLoading}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-4 py-2 rounded transition-colors text-sm"
            >
              Add Single Tile
            </button>

            <button
              onClick={applyPatternFill}
              disabled={!tileImageObj || isLoading}
              className="bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 text-white px-4 py-2 rounded transition-colors text-sm"
            >
              Pattern Fill
            </button>

            <button
              onClick={createGridPattern}
              disabled={!tileImageObj || isLoading}
              className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white px-4 py-2 rounded transition-colors text-sm"
            >
              Grid Pattern
            </button>

            <button
              onClick={clearTiles}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded transition-colors text-sm"
            >
              Clear Tiles
            </button>
          </div>
        </div>
      </div>

      {/* Preview Images */}
      {(wallImage || tileImage) && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {wallImage && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Wall Preview:</h4>
              <img
                src={wallImage}
                alt="Wall"
                className="w-full h-32 object-cover rounded border"
              />
            </div>
          )}

          {tileImage && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Tile Preview:</h4>
              <img
                src={tileImage}
                alt="Tile"
                className="w-full h-32 object-cover rounded border"
              />
            </div>
          )}
        </div>
      )}

      {/* Canvas */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold text-gray-800 mb-3">📋 Visualization Canvas</h3>

        {isLoading && (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-2 text-gray-600">Processing...</p>
          </div>
        )}

        <div className="flex justify-center">
          <canvas
            ref={canvasRef}
            className="border-2 border-gray-400 shadow-lg rounded bg-white"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        </div>

        <div className="mt-3 text-center text-sm text-gray-600">
          <div className="flex justify-center gap-4">
            <span>Total Tiles: {tiles.length}</span>
            {wallImageObj && <span className="text-green-600">Wall: ✓</span>}
            {tileImageObj && <span className="text-blue-600">Tile: ✓</span>}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 text-sm text-gray-600 bg-yellow-50 p-4 rounded-lg">
        <h4 className="font-semibold mb-2">📝 Instructions:</h4>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Step 1:</strong> Wall image upload करें (background के लिए)</li>
          <li><strong>Step 2:</strong> Tile image upload करें (जो tile लगाना है)</li>
          <li><strong>Step 3:</strong> कोई button click करें:
            <ul className="list-disc list-inside ml-4 mt-1">
              <li><strong>Add Single Tile:</strong> एक tile random position पर add होगी</li>
              <li><strong>Pattern Fill:</strong> पूरे canvas पर tiles भर जाएंगे</li>
              <li><strong>Grid Pattern:</strong> Organized grid में tiles लगेंगे</li>
            </ul>
          </li>
          <li><strong>Step 4:</strong> Result canvas में दिखेगा - अब images clear दिखेंगी!</li>
        </ul>
      </div>
    </div>
  );
}

export default TileVisualizer;